package com.dynamicruntime.common.gedra

import com.dynamicruntime.common.util.isAsciiLetterOrDigit
import com.dynamicruntime.common.util.isAsciiLetter
import com.dynamicruntime.common.exception.KdrException
import com.dynamicruntime.common.intern.Internable
import com.dynamicruntime.common.util.ConvProblem
import com.dynamicruntime.common.util.Parsed

/**
 * The pieces of a [GedraId]'s text form that are fixed by the format rather than supplied (issue #287).
 */
@Suppress("ConstPropertyName")
object GID {
    /** Separates the four parsed segments: storage, kind, client, base id. */
    const val partSep = '.'

    /** Introduces the optional trailing suffix — a child's index, or a config revision. */
    const val suffixSep = '~'

    /**
     * The reserved client that owns application-level data: the parent of all clients.
     *
     * Not to be confused with `ROLE.allClients` / `AdminScope.allClients`, which sound identical and are a
     * different axis entirely. Those say who may *read* across clients; this says who *owns* a row. A caller
     * with the capability reaches every client's data; data in the global client belongs to no one client.
     */
    const val globalClient = "global"

    /**
     * Every kind abbreviation is exactly this long, which is why `userData` abbreviates to `ud` and not `u`.
     * It makes the first six characters of every id a fixed-width prefix (`gd.fd.`, `gd.ud.`, `gc.wf.`), so a
     * column of ids lines up when read by eye — which is most of how ids are read. The value evaporates the
     * first time an abbreviation is a different length, so "checkKindAbbrevs" enforces it.
     */
    const val kindAbbrevLength = 2
}

/**
 * Which family of tables a gedra lives in — the first segment of a [GedraId].
 *
 * Also the switch that decides which enum the *second* segment is drawn from, which is why the two kind
 * enums have to be reachable through one interface: see [GedraKind].
 */
@Suppress("EnumEntryName")
enum class GedraStorageType(val idAbbrev: String) {
    /** Ordinary stored entities: form documents, workflow data, users, file references. */
    dataStore("gd"),

    /**
     * Definitions — clients, workflows, traits — which are revisioned and pinned per environment.
     *
     * Reserved here so the abbreviation cannot be claimed by anything else and the intent is visible. Its one
     * kind is [GedraConfigType.configDoc] (issue #292): a bundle of definitions, which config *traits* bind to
     * (issue #316) the way data traits bind to a [GedraDataType].
     */
    configStore("gc"),
}

/**
 * What a gedra *is* — the second segment of a [GedraId], drawn from [GedraDataType] or (later) the config
 * equivalent depending on the first.
 *
 * One interface over both enums rather than a nullable field per enum on [GedraId]. Two nullable fields make
 * three illegal states representable — both set, neither set, and one that disagrees with the storage type —
 * each prevented only by a rule somebody has to remember. Here [storageType] is *derived* from the kind, so it
 * cannot contradict it, and there is no state to validate.
 *
 * **The split question is closed (issue #316).** #298 recorded that if traits bound to data kinds only, this
 * interface would have one implementer's worth of purpose -- the evidence that would have justified splitting
 * [GedraId] into data and config variants, a split #292 decided against. Config traits ([GedraConfigTrait])
 * bind to [GedraConfigType] through it, so it has its second real user, and [GedraId] stays one class.
 */
interface GedraKind {
    /** This kind's two-character abbreviation in an id; see [GID.kindAbbrevLength]. */
    val idAbbrev: String

    /** The table family this kind is stored in — fixed per enum, not per entry. */
    val storageType: GedraStorageType
}

/**
 * The kinds of gedra stored as data. The names are the ones settled for the entity types themselves, so the
 * enum and the vocabulary cannot drift: `userData` rather than `user`, `formDoc` rather than "document",
 * `wfData` as the half of "workflow" that is captured data rather than rules.
 *
 * `wfDef` is deliberately absent. A workflow *definition* is config, and #292 settled that definitions travel
 * together in one [GedraConfigType.configDoc] bundle rather than each as a kind of its own -- so there is no
 * `gc.wf.` id, and a definition is addressed inside its bundle. `wfData` keeps the `wf` abbreviation for the
 * half of "workflow" that is captured data rather than rules.
 */
@Suppress("EnumEntryName")
enum class GedraDataType(override val idAbbrev: String) : GedraKind {
    /** User/account data. */
    userData("ud"),

    /** A single stored entity holding form-entry data. */
    formDoc("fd"),

    /**
     * Data captured while a workflow was followed. Nothing creates one yet (every create and save makes a [formDoc]),
     * but it is what a "true" workflow will create, and it is on the roadmap: reserved, not vestigial.
     */
    wfData("wf"),

    /** A pointer to (and claim on) file content held in a file store. */
    fileRef("fr");

    override val storageType: GedraStorageType get() = GedraStorageType.dataStore
}

/**
 * The kinds of gedra stored as config — definitions, which are revisioned and pinned per environment.
 *
 * One kind for now, and that is the direction #292 settled rather than a placeholder: traits and workflows
 * are defined *together* in one bundle, so that `$ref`s and workflow-to-trait links stay mostly inside a
 * single object rather than spreading across many. What would have been several config kinds is one.
 *
 * `configDoc` echoes `formDoc`, and abbreviates to `cd` rather than reusing the storage segment's `gc` —
 * `gc.cd.` says storage then kind the way `gd.fd.` does, where `gc.gc.` would leave the kind segment
 * carrying no information at all.
 */
@Suppress("EnumEntryName")
enum class GedraConfigType(override val idAbbrev: String) : GedraKind {
    /** A bundle of definitions: traits, workflows, and optionally its client's definition ([GedraConfig]). */
    configDoc("cd");

    override val storageType: GedraStorageType get() = GedraStorageType.configStore
}

/**
 * Where a randomly generated base id came from — the single letter it starts with.
 *
 * It is never parsed back out. Its whole job is that somebody looking at an id, or at a column of them, can
 * see how the objects got there: a run of `e`s is an import, a `u` is somebody working in the UI. Expect this
 * list to grow as more things create gedras.
 */
@Suppress("EnumEntryName")
enum class GedraIdContext(val letter: String) {
    /** Somebody working in the application's own UI. */
    ui("u"),

    /** A spreadsheet import. */
    excel("e"),

    /** An API caller. */
    api("a"),

    /** The runtime itself — bootstrapping, migrations, scheduled work. */
    system("s"),

    /** Created by a test. */
    test("t"),
}

/**
 * The identity of a gedra: `<storage>.<kind>.<client>.<baseId>` with an optional `~<suffix>` (issue #287).
 *
 * ```
 * gd.fd.acme.e20260812130405123AbCd~7
 * ~~ ~~ ~~~~ ~~~~~~~~~~~~~~~~~~~~~~ ~
 * |  |  |    |                      the 7th row of the import that produced it
 * |  |  |    an opaque base id -- `e` says an Excel import made it
 * |  |  the owning client
 * |  a form document
 * stored as data
 * ```
 *
 * ### What is parsed and what is not
 *
 * The first three segments are structure and are read back out. **The base id is opaque** — it is built to be
 * invariant given its inputs, not to be taken apart. A random one is a context letter followed by the
 * project's standard time-sortable unique id; a deterministic one spells out what makes it unique (`u12` for a
 * user's gedra, `u12_acmePaymentWf` for the one workflow a user may have). Nothing reads either back.
 *
 * The [suffix] is not parsed either, but it *is* held separately, because for config it is the revision and
 * "same object, which revision" is a question worth being able to ask without string surgery. For config that
 * revision is a whole number, checked on the way in (`checkSuffix`), so `~3` collates with `~2` and `~4` as
 * versions of one config; [revision] reads it back and [revisionClass] drops it (issue #612).
 *
 * ### A present suffix is a pin, not an instruction
 *
 * An id with **no** suffix means the active revision. An id *with* one that is stored somewhere -- a `WfRef` on a
 * gedra, recording which bundle revision shaped it -- is **lineage**: it says which revision was current when
 * the reference was written, for audit and diagnosis. Execution always resolves against the **active** revision
 * of the class; nothing runs a superseded revision because a stored reference names it. Recorded here (issue
 * #612) because the id is where "which revision?" is asked, and #614 reads revisions by this rule.
 *
 * ### Interning, and why parsing does not do it
 *
 * A `GedraId` is [Internable], and ids for gedras that exist are interned so that `===` is a legitimate
 * identity test and a cache miss answers "no such gedra" without a database round trip (issue #280).
 *
 * [parse] deliberately does **not** intern. It is fed whatever a caller sends, and a well-formed id for an
 * object that was never created is still well-formed — interning those would fill the cache with ids for
 * things that do not exist and destroy the property that makes the cache worth having. Interning is for ids
 * being created or loaded. The consequence, and it matters: [equals] is real value equality on [fullId], not
 * identity, so an id that came from [parse] still behaves correctly as a map key and still equals its
 * interned twin.
 *
 * ### Text form
 *
 * `.` and `~` are both *unreserved* in RFC 3986, so an id needs no escaping in a URL. The cost, accepted
 * knowingly: both are split points for a full-text log tokenizer, so a whole id pasted into a log search does
 * not stay one token the way `mkUniqueId`'s output was carefully built to. The [baseId] does, though — it is
 * held to `[A-Za-z0-9_]` for exactly that reason — and it is the piece anyone hunting a single object would
 * paste.
 *
 * A **sandbox** client puts a `:` in the client segment (`gd.fd.acme:sandbox.e2026…`, issue #927; see [SBX]).
 * A colon is not unreserved, but it is legal unescaped in a URL path segment, a query, and a fragment, which is
 * everywhere an id travels; `encodeURIComponent` escapes it and every reader decodes. It is not a separator
 * here, so parsing is unchanged.
 */
class GedraId private constructor(
    /** What this gedra is; [storageType] follows from it. */
    val kind: GedraKind,
    /** The owning client, or [GID.globalClient] for application-level data. */
    val client: String,
    /** The opaque unique part. Never parsed; see the class note. */
    val baseId: String,
    /** A child's index within its imported parent, or a config revision. Absent for most ids. */
    val suffix: String?,
    /** The canonical text form — the intern key, the `toString`, and what is stored and transmitted. */
    val fullId: String,
) : Internable {

    /** Which table family this gedra lives in. Derived, so it can never disagree with [kind]. */
    val storageType: GedraStorageType get() = kind.storageType

    /** [kind] as a data kind, or null when this is a config id. */
    val dataType: GedraDataType? get() = kind as? GedraDataType

    /**
     * For a config id, the revision its [suffix] names, or null when it names none -- the **active** revision
     * (issue #612). Always null for a data id, whose suffix is a child index rather than a revision. Safe to read
     * as a number because a config suffix is checked to be one on the way in (`checkSuffix`).
     */
    val revision: Int? get() = if (storageType == GedraStorageType.configStore) suffix?.toInt() else null

    /**
     * The **revision class** of this id (issue #612): the same config with no revision -- `gc.cd.acme.main` for
     * `gc.cd.acme.main~3` -- which collates every revision of one config. The config cache (#615) keys on it. An
     * id with no suffix is its own class.
     *
     * Config ids only -- a data id's suffix is a child index, not a revision, so dropping it would forge a
     * different-looking id rather than answer a question. Guarded to fail loudly rather than return a plausible
     * wrong id, the same config/data line [revision] draws.
     */
    fun revisionClass(): GedraId {
        requireConfig("revisionClass")
        return if (suffix == null) this else of(kind, client, baseId)
    }

    /**
     * This id at [revision] (issue #612): `gc.cd.acme.main~3` from any revision of `gc.cd.acme.main`. Config ids
     * only, for the reason [revisionClass] is -- a data id has no revision to set.
     */
    fun withRevision(revision: Int): GedraId {
        requireConfig("withRevision")
        return of(kind, client, baseId, revision.toString())
    }

    /** Refuses a config-only operation on a data id, naming it, so misuse fails where it is written. */
    private fun requireConfig(operation: String) {
        if (storageType != GedraStorageType.configStore) {
            throw KdrException.mkInput("'$operation' is a config-id operation; '$fullId' is a data id.")
        }
    }

    override fun toInternString(): String = fullId

    override fun toString(): String = fullId

    /**
     * Value equality on [fullId], not identity.
     *
     * Interning makes `===` *usable* for ids known to exist; it does not make it the contract. [parse] hands
     * back uninterned instances by design, and one of those has to equal its interned twin or a caller that
     * parsed an id could not find the gedra it names in a map keyed by interned ones.
     */
    override fun equals(other: Any?): Boolean = this === other || (other is GedraId && other.fullId == fullId)

    override fun hashCode(): Int = fullId.hashCode()

    companion object {
        /**
         * Builds an id from its parts, validating each. The text form is built here, so a caller that already
         * holds the parts never pays to parse a string it just assembled.
         */
        fun of(kind: GedraKind, client: String, baseId: String, suffix: String? = null): GedraId {
            checkClient(client)
            checkPart(baseId, "base id")
            suffix?.let { checkSuffix(kind, it) }
            val fullId = buildString {
                append(kind.storageType.idAbbrev).append(GID.partSep)
                append(kind.idAbbrev).append(GID.partSep)
                append(client).append(GID.partSep)
                append(baseId)
                if (suffix != null) append(GID.suffixSep).append(suffix)
            }
            return GedraId(kind, client, baseId, suffix, fullId)
        }

        /**
         * Reads an id from its text form. Structural only — a well-formed id says nothing about whether the
         * gedra exists, and this does not intern (see the class note).
         *
         * The supplied string becomes [fullId] unchanged rather than being rebuilt: every segment has been
         * validated, so re-assembling them could only produce the same characters back.
         */
        fun parse(fullId: String): GedraId = parseResult(fullId).orThrow { KdrException.mkInput(it) }

        /** [parse], or null for a string that is not a well-formed gedra id (issue #909). */
        fun parseOrNull(fullId: String): GedraId? = parseResult(fullId).valueOrNull()

        /** [parse]'s outcome as a value (issue #909): the id, or what is wrong with the string. Never throws. */
        fun parseResult(fullId: String): Parsed<GedraId> {
            // limit = 4: the base id is opaque and the suffix is split off afterward, so anything past the
            // third separator belongs to the caller's own construction and is not ours to divide further.
            val parts = fullId.split(GID.partSep, limit = 4)
            if (parts.size != 4) {
                return bad(fullId, "it needs four '${GID.partSep}'-separated parts")
            }
            val storageType = GedraStorageType.entries.firstOrNull { it.idAbbrev == parts[0] }
                ?: return bad(fullId, "'${parts[0]}' is not a storage type (${abbrevList()})")
            val kinds = kindsOf(storageType)
            // Reachable only for a storage type declared before its kinds are -- which `configStore` itself
            // was between #287 and #297, so it is a real state rather than a hypothetical one.
            if (kinds.isEmpty()) {
                return bad(fullId, "'${parts[0]}' ids are not supported yet")
            }
            val kind = kinds.firstOrNull { it.idAbbrev == parts[1] }
                ?: return bad(
                    fullId,
                    "'${parts[1]}' is not a '${parts[0]}' kind (${kinds.joinToString(", ") { it.idAbbrev }})",
                )
            val client = parts[2]
            val tail = parts[3]
            val at = tail.indexOf(GID.suffixSep)
            val baseId = if (at < 0) tail else tail.substring(0, at)
            val suffix = if (at < 0) null else tail.substring(at + 1)
            (clientProblem(client) ?: partProblem(baseId, "base id") ?: suffix?.let { suffixProblem(kind, it) })
                ?.let { return Parsed.failed(ConvProblem.badFormat, it) }
            return Parsed.Ok(GedraId(kind, client, baseId, suffix, fullId))
        }

        /** The kinds belonging to [storageType]; empty for a storage type with no kinds defined yet. */
        fun kindsOf(storageType: GedraStorageType): List<GedraKind> = when (storageType) {
            GedraStorageType.dataStore -> GedraDataType.entries
            GedraStorageType.configStore -> GedraConfigType.entries
        }

        /**
         * Verifies the fixed-width rule [GID.kindAbbrevLength] states. Called by the tests rather than at
         * every parse: it is a property of the enums, so it can only be broken by editing one, and a check
         * that runs on every id would be paying at runtime for a mistake that cannot survive a build.
         */
        fun checkKindAbbrevs(): List<String> = GedraStorageType.entries
            .flatMap { kindsOf(it) }
            .map { it.idAbbrev }
            .filter { it.length != GID.kindAbbrevLength }

        private fun abbrevList() = GedraStorageType.entries.joinToString(", ") { it.idAbbrev }

        /**
         * A client name is a Java-variable-friendly identifier. Config objects are named by people and
         * addressed by their names, so the names have to survive being used as code identifiers.
         *
         * The one exception is a **sandbox** id (issue #927), `<parent>:sandbox`: the parent is held to the same
         * rule, and the colon and role are the system's, never an author's (see [SBX]). A colon in any other
         * form is refused, which is also what refuses a sandbox of a sandbox; so is a sandbox of the global
         * client, which has none.
         */
        private fun checkClient(client: String) {
            clientProblem(client)?.let { throw KdrException.mkInput(it) }
        }

        /** What is wrong with [client] as an id's client segment, or null when nothing is; see [checkClient]. */
        private fun clientProblem(client: String): String? {
            val parent = sandboxParentOf(client)
            if (parent == null && SBX.roleSep in client) {
                return "A gedra client may hold a colon only as a sandbox id, '<parent>${SBX.suffix}'; " +
                    "'$client' is not one."
            }
            if (parent == GID.globalClient) {
                return "The '${GID.globalClient}' client has no sandbox; '$client' names one."
            }
            val name = parent ?: client
            if (name.isEmpty() || !(name[0].isAsciiLetter() || name[0] == '_')) {
                return "A gedra client must start with a letter or underscore; '$client' does not."
            }
            return partProblem(name, "client")
        }

        /**
         * A suffix is a part like any other, and for a **config** id it is also a revision -- a whole number,
         * spelled the one way its integer prints, so `~3`, `~03` and `~+3` cannot be three ids for one revision
         * (issue #612). A data id's suffix is a child index and stays free-form: nothing reads it back, and
         * tightening it would refuse ids already stored.
         */
        private fun checkSuffix(kind: GedraKind, suffix: String) {
            suffixProblem(kind, suffix)?.let { throw KdrException.mkInput(it) }
        }

        /** What is wrong with [suffix] for an id of [kind], or null when nothing is; see [checkSuffix]. */
        private fun suffixProblem(kind: GedraKind, suffix: String): String? {
            partProblem(suffix, "suffix")?.let { return it }
            if (kind.storageType == GedraStorageType.configStore) {
                // `partProblem` has already refused any non-`[A-Za-z0-9_]` character, so the sign is gone and a
                // parse can only be a non-negative integer; the toString round-trip is what rejects a leading
                // zero (`03`), and a null is overflow past Int.
                val n = suffix.toIntOrNull()
                if (n == null || n.toString() != suffix) {
                    return "A config gedra's suffix is its revision and has to be a whole number, not '$suffix'."
                }
            }
            return null
        }

        /**
         * Held to `[A-Za-z0-9_]`, which is what every id-building path here produces and what keeps a base id
         * a single token to a log tokenizer (see the class note). Rejecting anything else also keeps the two
         * separators out, which is what makes the format unambiguous to parse.
         */
        private fun checkPart(part: String, what: String) {
            partProblem(part, what)?.let { throw KdrException.mkInput(it) }
        }

        /** What is wrong with [part] as an id segment named [what], or null when nothing is; see [checkPart]. */
        private fun partProblem(part: String, what: String): String? {
            if (part.isEmpty()) {
                return "A gedra $what may not be empty."
            }
            // ASCII rather than isLetterOrDigit(), which is Unicode-aware: an id is ASCII by construction, and a base
            // id that admitted Cyrillic lookalikes would be a way to mint two ids that read identically to a person
            // and differ to the cache.
            val bad = part.firstOrNull { !(it.isAsciiLetterOrDigit() || it == '_') }
            if (bad != null) {
                return "A gedra $what may hold only letters, digits and underscores; '$part' holds '$bad'."
            }
            return null
        }

        private fun bad(fullId: String, why: String): Parsed.Failed =
            Parsed.failed(ConvProblem.badFormat, "'$fullId' is not a gedra id: $why.")

    }
}
